import { LazoCheckbox } from "@/components/common/form/LazoCheckbox";
import Avatar from "@/components/lazo-ai-chat-components/Avatar";
import LazoTooltip from "@/components/utils/LazoTooltip";
import { CommonStyledSelect } from "@/components/utils/SelectStyles";
import { Colors } from "@/enums/Colors";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import { TinyUser } from "@/types/TinyUser";
import { FilterCategoryType } from "@/utils/FiltersUtils";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import { Close, ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Button,
  ButtonProps,
  SelectChangeEvent,
  SelectProps,
  SxProps,
  Typography,
  checkboxClasses,
  menuItemClasses,
  svgIconClasses,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Box, styled } from "@mui/system";
import { produce } from "immer";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import CirclesFourSVG from "../../../../assets/images/CirclesFour.svg";

export const StyledSelectCustomerPage = styled(({ children, ...props }: SelectProps) => (
  <CommonStyledSelect
    MenuProps={{
      PaperProps: {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        sx: {
          [`& .${menuItemClasses.focusVisible}`]: {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          [`& .${menuItemClasses.root}`]: {
            fontSize: "0.75rem",
            fontWeight: "400",
            justifyContent: "space-between",
            color: Colors.BLACK_151517,
            padding: "0 0 0 0.75rem",
            margin: 0,
            "&:nth-of-type(1)": {
              borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
            },
            [`& .${checkboxClasses.root}`]: {
              [`& .${svgIconClasses.root}`]: {
                fontSize: "1.3rem",
              },
            },
          },
          marginTop: "0.25rem",
          gap: "0.5rem",
          display: "flex",
          flexDirection: "column",
          borderRadius: "0.5rem",
          width: "13.75rem",
          maxHeight: "32rem",
          boxShadow: "0 0 0.938rem 0 rgba(0, 0, 0, 0.2)",
        },
      },
    }}
    {...props}
  >
    {children}
  </CommonStyledSelect>
))(() => ({
  color: Colors.BLACK_151517,
  height: "2.5rem",
  minHeight: 0,
  "& .MuiSelect-select": {
    padding: "0 0.5rem",
    maxWidth: "13.75rem",
    display: "flex",
    alignItems: "center",
    ":hover": {
      cursor: "pointer",
    },
    "&.MuiInputBase-input": {
      "&.MuiOutlinedInput-input": {
        paddingRight: "0",
      },
    },
  },
  [`& .${svgIconClasses.root}`]: {
    fontSize: "1.25rem",
  },
}));

const StyledTypography = styled(Typography)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "0.75rem",
}));

const KamContainer = styled(Typography)(() => ({
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  "& > p.name": {
    maxWidth: "7.5rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.75rem",
    fontWeight: "400",
  },
}));

type FilterSelectProps = {
  filterCategory: FilterCategoryType<unknown>;
  setSelectedFilters: Dispatch<SetStateAction<FilterCategoryType<unknown>[]>>;
  selectedCategoryFilters: string[];
  setAddedFilterCategories?: Dispatch<SetStateAction<FilterCategoryType<unknown>[]>>;
  hideRemove?: boolean;
  selectSx?: SxProps;
  defaultValue?: string[];
  hideCategory?: boolean;
};

function RemoveFilterButton(props: ButtonProps) {
  return (
    <Button sx={{ padding: 0, minWidth: "fit-content" }} {...props}>
      <Close />
    </Button>
  );
}

function arePropsEqual(oldProps: FilterSelectProps, newProps: FilterSelectProps): boolean {
  return (
    oldProps.filterCategory === newProps.filterCategory &&
    oldProps.setSelectedFilters === newProps.setSelectedFilters &&
    oldProps.selectedCategoryFilters === newProps.selectedCategoryFilters &&
    oldProps.setAddedFilterCategories === newProps.setAddedFilterCategories
  );
}

function SelectFilter(props: FilterSelectProps) {
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (props.defaultValue) {
      props.setSelectedFilters(
        produce((draft) => {
          draft.find((f) => f.id === props.filterCategory.id).filters = props.defaultValue as string[];
        })
      );
    }
  }, []);

  const handleChange = (ev: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = ev;
    if (value[value.length - 1] === "All") {
      handleAllClick();
    } else {
      props.setSelectedFilters(
        produce((draft) => {
          draft.find((f) => f.id === props.filterCategory.id).filters = value as string[];
        })
      );
    }
  };

  const handleRemoveFilter = () => {
    props.setAddedFilterCategories((prev) => prev.filter((cat) => cat.id !== props.filterCategory.id));
    props.setSelectedFilters((prev) => prev.filter((f) => f.id !== props.filterCategory.id));
  };

  const renderValue = (v: string[]) => {
    let text: string;
    if (v.length === 0) {
      text = "Nothing";
    } else if (v.length === props.filterCategory.filters.length) {
      text = "All";
    } else {
      if (props.filterCategory.id === "kam" || props.filterCategory.id === "serviceTeam" || props.filterCategory.id === "people") {
        text = v.length.toString();
      } else {
        text = v.map((v) => EnumsUtils.parseEnumToRender(v)).join(", ");
      }
    }

    return (
      <>
        <Box
          sx={{
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            width: "100%",
          }}
        >
          {!props.hideRemove && <RemoveFilterButton onClick={handleRemoveFilter} />}
          <StyledTypography width="100%" onClick={() => setOpen(true)}>
            {!props.hideCategory && (
              <Typography fontSize="0.75rem" color={Colors.LIGHT_GRAY_8A8E93}>
                {props.filterCategory.render}:
              </Typography>
            )}
            ({text})
          </StyledTypography>
        </Box>
      </>
    );
  };

  const handleAllClick = () => {
    if (props.selectedCategoryFilters.length === 0) {
      props.setSelectedFilters(
        produce((draft) => {
          draft.find((c) => c.id === props.filterCategory.id).filters = [...props.filterCategory.filters];
        })
      );
    } else {
      props.setSelectedFilters(
        produce((draft) => {
          draft.find((c) => c.id === props.filterCategory.id).filters = [];
        })
      );
    }
  };

  const endAdornment = () => {
    if (!open) {
      return <ExpandMore sx={{ ":hover": { cursor: "pointer" } }} onClick={() => setOpen(true)} />;
    } else {
      return <ExpandLess sx={{ ":hover": { cursor: "pointer" } }} onClick={() => setOpen(true)} />;
    }
  };

  const buildMenuItems = () => {
    switch (props.filterCategory.id) {
      case "people":
      case "kam":
        return props.filterCategory?.filtersRenders?.map((fr: User) => (
          <MenuItem key={fr.externalId} value={fr.externalId}>
            <KamContainer>
              <Avatar
                user={fr}
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  fontSize: "0.75rem",
                }}
              />
              <Typography className="name">{fr.firstName + " " + fr.lastName}</Typography>
            </KamContainer>
            <LazoCheckbox checked={props.selectedCategoryFilters.indexOf(fr.externalId) > -1} />
          </MenuItem>
        ));
      case "role":
      case "roles":
        return props.filterCategory?.filtersRenders?.map((f: string, idx: number) => (
          <MenuItem key={f} value={props.filterCategory.filters[idx]}>
            {f}
            <LazoCheckbox checked={props.selectedCategoryFilters.indexOf(props.filterCategory.filters[idx]) > -1} />
          </MenuItem>
        ));
      case "serviceTeam":
        return props.filterCategory?.filtersRenders?.map((lu: TinyUser) => (
          <MenuItem key={lu.firstName} value={lu.externalId}>
            <KamContainer>
              <Avatar
                user={lu}
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  fontSize: "0.75rem",
                }}
              />
              <Typography className="name">{lu.firstName + " " + lu.lastName}</Typography>
            </KamContainer>
            <LazoCheckbox checked={props.selectedCategoryFilters.indexOf(lu.externalId) > -1} />
          </MenuItem>
        ));
      case "tenant":
        return props.filterCategory.filters.map((f) => (
          <MenuItem key={f} value={f}>
            <LazoTooltip title={EnumsUtils.parseEnumToRender(f)}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{EnumsUtils.parseEnumToRender(f)}</span>
            </LazoTooltip>
            <LazoCheckbox checked={props.selectedCategoryFilters.indexOf(f) > -1} />
          </MenuItem>
        ));
      default:
        return props.filterCategory.filters.map((f) => (
          <MenuItem key={f} value={f}>
            <LazoTooltip title={EnumsUtils.parseEnumToRender(f)}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{EnumsUtils.parseEnumToRender(f)}</span>
            </LazoTooltip>
            <LazoCheckbox checked={props.selectedCategoryFilters.indexOf(f) > -1} />
          </MenuItem>
        ));
    }
  };

  return (
    <StyledSelectCustomerPage
      multiple={true}
      value={props.selectedCategoryFilters}
      displayEmpty
      onChange={handleChange}
      IconComponent={null}
      endAdornment={endAdornment()}
      open={open}
      onClose={() => setOpen(false)}
      renderValue={(v) => renderValue(v as string[])}
      sx={props.selectSx}
    >
      <MenuItem key={"all-button"} value={"All"}>
        <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <img src={CirclesFourSVG.src} alt={"circles-four"} />
          All
        </Box>
        <LazoCheckbox
          indeterminate={
            props.selectedCategoryFilters.length !== props.filterCategory.filters.length && props.selectedCategoryFilters.length !== 0
          }
          checked={props.filterCategory.filters.length === props.selectedCategoryFilters.length}
        />
      </MenuItem>
      {buildMenuItems()}
    </StyledSelectCustomerPage>
  );
}

export default React.memo(SelectFilter, arePropsEqual);
